import { AppModuleCode, PermissionCode, userSalutationName } from '@gb-ticket/shared-types';
import {
  BookOpenIcon,
  HelpCircleIcon,
  LifeBuoyIcon,
  PlusIcon,
  StarIcon,
  TicketIcon,
} from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { hasPermission, isModuleEnabled } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useVoiceCopy, useVoice } from '@/platform/voice/use-voice';
import { useCsatPending } from '@/products/helpdesk/features/csat/use-csat';
import { StatusBadge } from '@/products/helpdesk/features/tickets/ticket-badges';
import { useTickets } from '@/products/helpdesk/features/tickets/use-tickets';
import { PORTAL_ROOT } from '@/products/helpdesk/features/portal/portal-paths';
import { formatDateTime } from '@/lib/format';
import { usePageTitle } from '@/hooks/use-page-title';

export function PortalHomePage() {
  const copy = useVoiceCopy();
  usePageTitle('IT-Hilfe');
  const me = useMe();
  const voice = useVoice();
  const canCreate = hasPermission(me.data, PermissionCode.TICKETS_CREATE);
  const canSubmitCsat =
    hasPermission(me.data, PermissionCode.CSAT_SUBMIT) &&
    isModuleEnabled(me.data, AppModuleCode.CSAT);
  const showKnowledge =
    hasPermission(me.data, PermissionCode.KB_READ) &&
    isModuleEnabled(me.data, AppModuleCode.KNOWLEDGE);
  const pendingCsat = useCsatPending(canSubmitCsat);
  const recent = useTickets({ page: 1, pageSize: 5, sortBy: 'updatedAt', sortOrder: 'desc' });
  const salutation = me.data ? userSalutationName(me.data, voice) : '';

  return (
    <section className="space-y-8">
      <div className="max-w-2xl space-y-3">
        <p className="text-sm text-muted-foreground">IT-Hilfe · Self-Service</p>
        <h1 className="text-3xl font-bold tracking-tight">
          {salutation
            ? `Hallo, ${salutation} — womit können wir helfen?`
            : 'Hallo — womit können wir helfen?'}
        </h1>
        <p className="text-base text-muted-foreground">{copy('helpdesk.portal.home.lead')}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {showKnowledge && (
          <ShortcutCard
            to={`${PORTAL_ROOT}/wissen`}
            icon={BookOpenIcon}
            title="Hilfe finden"
            description="Anleitungen durchsuchen oder nach Thema blättern."
          />
        )}
        {showKnowledge && (
          <ShortcutCard
            to={`${PORTAL_ROOT}/faq`}
            icon={HelpCircleIcon}
            title="Häufige Fragen"
            description="Kurze Antworten auf die häufigsten Anliegen."
          />
        )}
        {canCreate && (
          <ShortcutCard
            to={`${PORTAL_ROOT}/tickets/neu`}
            icon={PlusIcon}
            title="Neue Anfrage"
            description={copy('helpdesk.portal.home.while-writing')}
            primary
          />
        )}
        <ShortcutCard
          to={`${PORTAL_ROOT}/tickets`}
          icon={TicketIcon}
          title="Meine Anfragen"
          description="Offene und erledigte Anfragen ansehen und ergänzen."
        />
      </div>

      {canSubmitCsat && pendingCsat.data && pendingCsat.data.items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <StarIcon className="size-5" aria-hidden />
              Kurze Bewertung
            </CardTitle>
            <CardDescription>{copy('helpdesk.portal.home.pending-csat')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {pendingCsat.data.items.map((survey) => (
                <li key={survey.id}>
                  <Link
                    to={`${PORTAL_ROOT}/tickets/${survey.ticketNumber}`}
                    className="flex flex-col gap-1 py-3 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-medium">
                        #{survey.ticketNumber} · {survey.ticketSubject}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {copy('helpdesk.portal.home.rate')}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>{copy('helpdesk.portal.home.recent')}</CardTitle>
            <CardDescription>{copy('helpdesk.portal.home.open-one')}</CardDescription>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to={`${PORTAL_ROOT}/tickets`}>Alle anzeigen</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recent.data?.items.length === 0 && (
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <LifeBuoyIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
              {showKnowledge
                ? copy('helpdesk.portal.home.none-faq')
                : copy('helpdesk.portal.home.none')}
            </p>
          )}
          {recent.data && recent.data.items.length > 0 && (
            <ul className="divide-y">
              {recent.data.items.map((ticket) => (
                <li key={ticket.id}>
                  <Link
                    to={`${PORTAL_ROOT}/tickets/${ticket.number}`}
                    className="flex flex-col gap-1 py-3 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-medium">
                        #{ticket.number} · {ticket.subject}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Zuletzt aktualisiert {formatDateTime(ticket.updatedAt)}
                      </p>
                    </div>
                    <StatusBadge status={ticket.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function ShortcutCard({
  to,
  icon: Icon,
  title,
  description,
  primary,
}: {
  to: string;
  icon: typeof BookOpenIcon;
  title: string;
  description: string;
  primary?: boolean;
}) {
  return (
    <Link to={to} className="group block h-full">
      <Card
        className={
          primary
            ? 'h-full border-brand-red/20 bg-brand-red text-white transition-shadow group-hover:shadow-md'
            : 'h-full transition-shadow group-hover:shadow-md'
        }
      >
        <CardHeader>
          <Icon className="mb-1 size-5" aria-hidden />
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription className={primary ? 'text-white/80' : undefined}>
            {description}
          </CardDescription>
        </CardHeader>
      </Card>
    </Link>
  );
}
